import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import CallMeBackPopup from '../CallMeBackPopup';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isCallMeBackOpen, setIsCallMeBackOpen] = useState(false);
  const location = useLocation();

  // Main navigation items for desktop navbar
  const mainNavItems = [
    { label: 'Popular Cities', path: '/popular-cities' },
    { label: 'Tour Packages', path: '/tour-packages' },
    { label: 'Tempo Traveller', path: '/tempo-traveller' },
    { label: 'Force Urbania', path: '/force-urbania' },
  ];

  // Our Services dropdown items
  const servicesDropdownItems = [
    { label: 'Local Cab', path: '/local-cab' },
    { label: 'Self Drive Car Rental Service', path: '/self-drive' },
    { label: 'Wedding Car', path: '/wedding-car' },
    { label: 'Yatra', path: '/yatra' },
    { label: 'Daily Pick & Drop Service', path: '/daily-pick-drop' },
    { label: 'Corporate Car Rental', path: '/corporate-car-rental' },
    { label: 'Monthly Cab Service', path: '/monthly-cab-service' },
  ];

  // All navigation items for mobile menu
  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'Popular Cities', path: '/popular-cities' },
    { label: 'Tour Packages', path: '/tour-packages' },
    { label: 'Tempo Traveller', path: '/tempo-traveller' },
    { label: 'Force Urbania', path: '/force-urbania' },
    { label: 'Local Cab', path: '/local-cab' },
    { label: 'Self Drive Car Rental', path: '/self-drive' },
    { label: 'Wedding Car', path: '/wedding-car' },
    { label: 'Yatra', path: '/yatra' },
    { label: 'Daily Pick & Drop', path: '/daily-pick-drop' },
    { label: 'Corporate Car Rental', path: '/corporate-car-rental' },
    { label: 'Our Cabs', path: '/our-cabs' },
    { label: 'Reviews', path: '/reviews' },
    { label: 'Blogs', path: '/blogs' },
    { label: 'FAQ', path: '/faq' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Contact Us', path: '/contact-us' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/917998666660', '_blank');
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:+917998666660';
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+917998666660';
  };

  const handleCallMeBack = () => {
    setIsCallMeBackOpen(true);
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 bg-white shadow-md z-50"
      >
        <div className="w-full px-0">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Desktop Layout: Hamburger + Logo (Left), Navigation (Center), Actions (Right) */}
            <div className="hidden lg:flex items-center justify-between w-full">
              {/* Left Section: Hamburger + Logo */}
              <div className="flex items-center gap-2 xl:gap-4 flex-shrink-0">
                {/* Yellow Hamburger Menu */}
                <button
                  onClick={handleMobileMenuToggle}
                  className="p-3 xl:p-4 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-200 h-14 xl:h-16 flex items-center justify-center"
                  aria-label="Toggle menu"
                  style={{ width: '48px' }}
                >
                  <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={22} className="text-gray-900" />
                </button>
                
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2 xl:gap-3 group ml-1 xl:ml-2">
                  <div className="relative">
                    <img 
                      src="/assets/images/WhatsApp_Image_2026-01-08_at_12.01.09_AM_1_-1768328157523.jpeg" 
                      alt="Advait Tour and Travels Logo" 
                      className="w-10 h-10 xl:w-12 xl:h-12 rounded-full object-cover relative z-10"
                    />
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-lg xl:text-xl font-bold text-blue-600">
                      Advait Tour
                    </span>
                    <span className="text-xs xl:text-sm font-semibold text-gray-700">
                      and Travels
                    </span>
                  </div>
                </Link>
              </div>

              {/* Center Section: Navigation Items */}
              <nav className="flex items-center gap-4 xl:gap-8 flex-1 justify-center">
                {mainNavItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`text-xs xl:text-sm font-semibold transition-colors duration-200 whitespace-nowrap uppercase ${
                      isActivePath(item?.path)
                        ? 'text-blue-600' : 'text-gray-800 hover:text-blue-600'
                    }`}
                  >
                    {item?.label}
                  </Link>
                ))}
                
                {/* Our Services Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                    className="flex items-center gap-1 text-xs xl:text-sm font-semibold text-gray-800 hover:text-blue-600 transition-colors duration-200 whitespace-nowrap uppercase"
                  >
                    <span>OUR SERVICES</span>
                    <Icon name={isServicesDropdownOpen ? "ChevronUp" : "ChevronDown"} size={16} className="transition-transform duration-300" />
                  </button>
                  
                  {isServicesDropdownOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-fade-in">
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
                      {servicesDropdownItems?.map((item) => (
                        <Link
                          key={item?.path}
                          to={item?.path}
                          onClick={() => setIsServicesDropdownOpen(false)}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:text-blue-700 transition-all duration-200 relative group"
                        >
                          <span className="relative z-10">{item?.label}</span>
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-gradient-to-b from-blue-500 to-blue-700 transition-all duration-200 group-hover:h-full rounded-r"></span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Call Me Back Link */}
                <button
                  onClick={handleCallMeBack}
                  className="text-xs xl:text-sm font-semibold text-gray-800 hover:text-blue-600 transition-colors duration-200 whitespace-nowrap uppercase"
                >
                  CALL ME BACK
                </button>
              </nav>

              {/* Right Section: Call and WhatsApp Buttons */}
              <div className="flex items-center gap-0 flex-shrink-0">
                {/* Call Button */}
                <button
                  onClick={handleCallClick}
                  className="flex items-center gap-1.5 xl:gap-2 px-3 xl:px-5 py-2 xl:py-3 text-white font-semibold transition-all duration-300 h-14 xl:h-16 hover:shadow-xl hover:scale-105"
                  style={{
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'gradientPulseCall 4s ease-in-out infinite'
                  }}
                >
                  <Icon name="Phone" size={16} className="text-white" />
                  <span className="text-xs xl:text-sm">+91-79986-666660</span>
                </button>
                
                {/* WhatsApp Button */}
                <button
                  onClick={handleWhatsAppClick}
                  className="flex items-center gap-1.5 xl:gap-2 px-3 xl:px-5 py-2 xl:py-3 text-white font-semibold transition-all duration-300 h-14 xl:h-16 hover:shadow-xl hover:scale-105"
                  style={{ 
                    borderTopLeftRadius: '0', 
                    borderBottomLeftRadius: '0',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'gradientPulseWhatsApp 4s ease-in-out infinite'
                  }}
                >
                  <Icon name="MessageCircle" size={16} className="text-white" />
                  <span className="text-xs xl:text-sm">WHATSAPP</span>
                </button>
              </div>
            </div>

            {/* Mobile Layout: Hamburger Left, Logo Center-Left, Book Now Right */}
            <div className="lg:hidden flex items-center justify-between w-full px-0 pr-2 sm:pr-3">
              {/* Left Side: Hamburger + Logo */}
              <div className="flex items-center gap-0">
                <button
                  onClick={handleMobileMenuToggle}
                  className="p-3 sm:p-4 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 transition-colors duration-200 h-14 sm:h-16 flex items-center justify-center touch-manipulation"
                  aria-label="Toggle menu"
                  style={{ width: '48px' }}
                >
                  <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} className="text-gray-900" />
                </button>
                <Link to="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-1.5 sm:ml-2">
                  <img 
                    src="/assets/images/WhatsApp_Image_2026-01-08_at_12.01.09_AM_1_-1768328157523.jpeg" 
                    alt="Advait Tour and Travels Logo" 
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-bold text-blue-600">
                      Advait Tour
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      and Travels
                    </span>
                  </div>
                </Link>
              </div>

              {/* Right Side: Book Now with color-changing animation */}
              <a
                href="tel:+917998666660"
                className="px-3 py-2 bg-yellow-400 text-gray-900 rounded font-bold text-xs transition-all duration-200 animate-color-shift-yellow"
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile/Desktop Menu - Now used for both mobile and desktop */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleMobileMenuToggle}
          />
          <div className="fixed top-0 left-0 bottom-0 w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <img 
                    src="/assets/images/WhatsApp_Image_2026-01-08_at_12.01.09_AM_1_-1768328157523.jpeg" 
                    alt="Advait Tour and Travels Logo" 
                    className="w-11 h-11 rounded-full object-cover shadow-md"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-base font-extrabold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                      Advait Tour
                    </span>
                    <span className="text-sm font-bold bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
                      and Travels
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleMobileMenuToggle}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <Icon name="X" size={24} className="text-gray-900" />
                </button>
              </div>

              <nav className="space-y-2 mb-6">
                {navigationItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={handleNavClick}
                    className={`block px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                      isActivePath(item?.path)
                        ? 'bg-blue-50 text-blue-600' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {item?.label}
                  </Link>
                ))}
              </nav>

              <div className="pt-6 border-t border-gray-200">
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => {
                    handleCallMeBack();
                    handleNavClick();
                  }}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                >
                  Call Me Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Me Back Popup */}
      <CallMeBackPopup 
        isOpen={isCallMeBackOpen} 
        onClose={() => setIsCallMeBackOpen(false)} 
      />
    </>
  );
};

export default Header;